import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import apiCall from "services/apiCall";
import { getSelectedAuthorization } from "store/slices/userSlice";
import {
  getStudentSurveyInfo,
  getConservatorSurveyInfo,
  searchPersonnelSurvey
} from "services/survey-service";
import { RoleTypeEnum } from "constants/enums/RoleEnum";
import SurveyModal from "components/SurveyModal/SurveyModal";

export const SurveyContext = createContext({
  pendingSurveys: [],
  completedSurveys: [],
  loading: false,
  showModal: false,
  selectedSurvey: null,
  isRequired: false,
  canRemindLater: true,
  showEnteranceModal: false,
  showSolveSurveyModal: false,
  fetchAssignedSurveys: () => null,
  completeSurvey: () => null,
  remindLater: () => null,
  checkPendingSurveys: () => null,
  openSurveyModal: () => null,
  closeSurveyModal: () => null,
  startSurvey: () => null,
  closeSolveSurveyModal: () => null,
  closeEnteranceModal: () => null
});

export const useSurveyContext = () => useContext(SurveyContext);

export default function SurveyProvider({ children }) {
  const auth = useSelector(getSelectedAuthorization);
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isRequired, setIsRequired] = useState(false);
  const [canRemindLater, setCanRemindLater] = useState(true);
  const [showSolveSurveyModal, setShowSolveSurveyModal] = useState(false);
  const [showEnteranceModal, setShowEnteranceModal] = useState(false);

  const surveyCheckRef = useRef(false);
  const lastShownSurveyIdRef = useRef(null);

  useEffect(() => {
    if (auth?.id) {
      fetchAssignedSurveys();
    }
  }, [auth?.id]);

  useEffect(() => {
    if (pendingSurveys.length > 0 && !surveyCheckRef.current && !showModal) {
      surveyCheckRef.current = true;
      checkAndShowRequiredSurvey();
    }
  }, [pendingSurveys, showModal]);

  const fetchAssignedSurveys = async () => {
    setLoading(true);
    surveyCheckRef.current = false;

    if (auth?.roleType === RoleTypeEnum.STUDENT) {
      await apiCall({
        service: getStudentSurveyInfo,
        serviceParams: auth?.id,
        onSuccess: (res) => {
          if (res?.data?.surveyList) {
            setPendingSurveys(res.data.surveyList || []);
          }
          if (res?.data?.answeredSurveyList) {
            setCompletedSurveys(res.data.answeredSurveyList || []);
          }
        },
        onError: (err) => {
          console.log("Survey fetch error:", err);
        }
      });
    } else if (auth?.roleType === RoleTypeEnum.CONSERVATOR) {
      await apiCall({
        service: getConservatorSurveyInfo,
        onSuccess: (res) => {
          if (res?.data?.list) {
            setPendingSurveys(res.data.list || []);
          }
        }
      });
    } else if (auth?.roleType === RoleTypeEnum.PERSONNEL) {
      await apiCall({
        service: searchPersonnelSurvey,
        onSuccess: (res) => {
          if (res?.data?.list) {
            setPendingSurveys(res.data.list || []);
          }
          if (res?.data?.answeredList) {
            setCompletedSurveys(res.data.answeredList || []);
          }
        },
        onError: (err) => {
          console.log("Personnel survey fetch error:", err);
        }
      });
    }

    setLoading(false);
  };

  const completeSurvey = async (surveyId, responseData) => {
    setLoading(true);

    const completed = pendingSurveys.find((survey) => survey.id === surveyId);
    if (completed) {
      setPendingSurveys((prev) => prev.filter((survey) => survey.id !== surveyId));
      setCompletedSurveys((prev) => [...prev, completed]);

      lastShownSurveyIdRef.current = surveyId;
      surveyCheckRef.current = false;
    }

    setLoading(false);
    return true;
  };

  const remindLater = async (surveyId) => {
    const now = new Date();
    const reminderTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    setLoading(true);

    setPendingSurveys((prev) =>
      prev.map((survey) =>
        survey.id === surveyId ? { ...survey, remindDate: reminderTime.toISOString() } : survey
      )
    );

    lastShownSurveyIdRef.current = surveyId;

    surveyCheckRef.current = false;

    setLoading(false);
    return true;
  };

  const checkAndShowRequiredSurvey = () => {
    if (showModal) return [];

    const currentDate = new Date();

    const requiredSurveys = pendingSurveys.filter(
      (survey) => survey.requiredSurvey === true && survey.id !== lastShownSurveyIdRef.current
    );

    if (requiredSurveys.length === 0) {
      return [];
    }

    const expiredReminders = requiredSurveys.filter((survey) => {
      if (survey.enableReminder === true && survey.reminderDate) {
        const reminderDate = new Date(survey.reminderDate);
        const endOfReminderDay = new Date(
          reminderDate.getFullYear(),
          reminderDate.getMonth(),
          reminderDate.getDate(),
          23,
          59,
          59,
          999
        );
        return endOfReminderDay < currentDate;
      }
      return false;
    });

    if (expiredReminders.length > 0) {
      const surveyToShow = expiredReminders[0];
      lastShownSurveyIdRef.current = surveyToShow.id;
      openSurveyModal(surveyToShow, true, false);
      return expiredReminders;
    }

    const availableRequired = requiredSurveys.filter((survey) => {
      if (survey.remindDate) {
        const remindDate = new Date(survey.remindDate);
        return remindDate <= currentDate;
      }

      return true;
    });

    if (availableRequired.length > 0) {
      const surveyToShow = availableRequired[0];

      const hasReminderEnabled = surveyToShow.enableReminder === true;
      const hasReminderExpired =
        hasReminderEnabled &&
        surveyToShow.reminderDate &&
        (() => {
          const reminderDate = new Date(surveyToShow.reminderDate);
          const endOfReminderDay = new Date(
            reminderDate.getFullYear(),
            reminderDate.getMonth(),
            reminderDate.getDate(),
            23,
            59,
            59,
            999
          );
          return endOfReminderDay < currentDate;
        })();

      const canRemind = hasReminderEnabled && !hasReminderExpired;

      lastShownSurveyIdRef.current = surveyToShow.id;
      openSurveyModal(surveyToShow, true, canRemind);
      return availableRequired;
    }

    return [];
  };

  const checkPendingSurveys = () => {
    return pendingSurveys.filter((survey) => survey.requiredSurvey === true);
  };

  const openSurveyModal = (survey, required = false, canRemind = true) => {
    if (!survey) return;

    setSelectedSurvey(survey);
    setIsRequired(required);
    setCanRemindLater(canRemind);
    setShowModal(true);
  };

  const startSurvey = () => {
    if (!selectedSurvey) return;

    setShowModal(false);

    setShowEnteranceModal(true);
  };

  const closeEnteranceModal = (startAnket = false) => {
    setShowEnteranceModal(false);

    if (startAnket && selectedSurvey) {
      setShowSolveSurveyModal(true);
    } else {
      setSelectedSurvey(null);
      surveyCheckRef.current = false;
    }
  };

  const closeSolveSurveyModal = () => {
    setShowSolveSurveyModal(false);

    if (selectedSurvey) {
      completeSurvey(selectedSurvey.id);
    }

    setSelectedSurvey(null);
    surveyCheckRef.current = false;
  };

  const closeSurveyModal = (survey, action) => {
    if (isRequired && !canRemindLater && action !== "start") {
      return;
    }

    setShowModal(false);

    if (action === "start" && survey) {
      startSurvey();
    } else if (action === "remind" && survey) {
      remindLater(survey.id);
    } else {
      surveyCheckRef.current = false;
    }

    if (action !== "start") {
      setSelectedSurvey(null);
      setIsRequired(false);
      setCanRemindLater(true);
    }
  };

  return (
    <SurveyContext.Provider
      value={{
        pendingSurveys,
        completedSurveys,
        loading,
        showModal,
        selectedSurvey,
        isRequired,
        canRemindLater,
        showSolveSurveyModal,
        showEnteranceModal,
        fetchAssignedSurveys,
        completeSurvey,
        remindLater,
        checkPendingSurveys,
        openSurveyModal,
        closeSurveyModal,
        startSurvey,
        closeSolveSurveyModal,
        closeEnteranceModal
      }}
    >
      {children}

      {/* Include the modal once */}
      <SurveyModal />
    </SurveyContext.Provider>
  );
}
